var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { LitElement, html } from 'lit';
import RadioGroup from 'devextreme/ui/radio_group';
import ScrollView from 'devextreme/ui/scroll_view';
import Tooltip from 'devextreme/ui/tooltip';
import { DateHelper } from '../../helpers/index';
/**
 * Represents the dinapsis-date-range-intervals component, that allow select
 * a date interval from a list and return a dates-changed event wiht start and
 * end date.
 */
let DinapsisDateRangeIntervals = class DinapsisDateRangeIntervals extends LitElement {
    constructor() {
        super();
        /**
         * The id of the div that the date-range-intervals is rendered in.
         */
        this.intervalsId = 'dinapsis-date-range-intervals';
        /**
         * RadioGroup component.
         */
        this.radioGroup = undefined;
        /**
         * Array of valid intervals.
         */
        this.validIntervals = [];
        /**
         * Value changed handler.
         * @param value Value of the event.
         */
        this.onValueChanged = ({ value }) => {
            if (value === null)
                return;
            if (value.backIntervalFromToday) {
                const result = DateHelper.getDateFromBackIntervalFromToday(value.backIntervalFromToday);
                this.startDate = result.startDate;
                this.endDate = result.endDate;
            }
            else {
                this.changeValueFromStartEndDate(value.startDate, value.endDate);
            }
            this.emitDatesChangedEvent();
        };
        this.intervals = [];
        const now = DateHelper.getYearMonthDayString(new Date());
        this.startDate = now;
        this.endDate = now;
        this.intervalSelected = -1;
    }
    /**
     * Reset the selected option of the RadioGroup component.
     */
    resetSelectedOption() {
        var _a;
        (_a = this.radioGroup) === null || _a === void 0 ? void 0 : _a.option('value', null);
        this.intervalSelected = -1;
    }
    /**
     * The above function is a lifecycle hook that is called when the component
     * is first updated. It is used to initialize the calendar view.
     */
    firstUpdated() {
        const scrollViewContainer = document.getElementById(`${this.intervalsId}-scrollview`);
        if (scrollViewContainer) {
            this.createScrollView(scrollViewContainer);
            const scrollViewContent = document.getElementById(`${this.intervalsId}-scrollview-content`);
            if (scrollViewContent) {
                this.createRadioGroup(scrollViewContent);
                this.createToolTipOfOverflowedRadioElement();
            }
        }
    }
    /**
     * Creates a tooltip in each radio option that the text overflow its container.
     */
    createToolTipOfOverflowedRadioElement() {
        const radioElements = document.querySelectorAll(`#${this.intervalsId} .dx-radiobutton .dx-item-content`);
        const maxHeightOfRadio = parseInt(getComputedStyle(radioElements[0]).maxHeight, 10);
        radioElements.forEach((item) => {
            if ((item === null || item === void 0 ? void 0 : item.scrollHeight) > maxHeightOfRadio) {
                item.append(this.generateTooltip(item));
            }
        });
    }
    /**
     * Generate Tooltip div.
     * @param item HTML element that show the tooltip when mouse hover.
     * @returns HTML element of tooltip.
     */
    generateTooltip(item) {
        const newTooltipContainer = document.createElement('div');
        newTooltipContainer.id = 'dinapsis-date-range-interval-tooltip-container';
        newTooltipContainer.className = 'my-tooltip-container';
        newTooltipContainer.innerText = item.innerText;
        const tooltip = new Tooltip(newTooltipContainer);
        tooltip.option('target', item);
        tooltip.option('showEvent', 'mouseenter');
        tooltip.option('hideEvent', 'mouseleave');
        return newTooltipContainer;
    }
    /**
     * Create a scroll view element.
     * @param container HTML container where to create the scroll view.
     */
    createScrollView(container) {
        const scrollView = new ScrollView(container);
        scrollView.option('width', '200px');
        scrollView.option('height', '250px');
        scrollView.option('direction', 'vertical');
        scrollView.option('showScrollbar', 'always');
    }
    /**
     * Create a radio group element.
     * @param container HTML container where to create the radio group.
     */
    createRadioGroup(container) {
        let interval = null;
        this.validateIntervals();
        if (this.intervalSelected > -1) {
            interval = this.validIntervals[this.intervalSelected];
        }
        this.radioGroup = new RadioGroup(container, { items: this.validIntervals });
        this.radioGroup.option('onValueChanged', this.onValueChanged);
        this.radioGroup.option('value', interval);
    }
    /**
     * Return an array of the valid intervals.
     * @param intervals Array of all intervals.
     * @returns Array of valid intervals.
     */
    validateIntervals() {
        this.intervals.forEach((item) => {
            if (item.backIntervalFromToday) {
                this.validateBackIntervalFromToday(item);
            }
            else {
                this.validateStartAndEndDate(item);
            }
        });
    }
    /**
     * Validates backIntervalFromToday interval expression.
     * @param interval IDateRangeInterval
     */
    validateBackIntervalFromToday(interval) {
        if (interval.startDate || interval.endDate) {
            console.error(`Not valid DateRange interval: "${interval.text}"\n  Cannot mix backIntervalFromToday and startDate / endDate`);
        }
        else if (!this.isValidBackIntervalFromToday(interval.backIntervalFromToday)) {
            console.error(`Not valid DateRange interval: "${interval.text}"\n  backIntervalFromToday: ${interval.backIntervalFromToday}`);
        }
        else {
            this.validIntervals.push(interval);
        }
    }
    /**
     * Validates start and end date.
     * @param interval IDateRangeInterval
     */
    validateStartAndEndDate(interval) {
        if (!this.isValidStartAndEndDate(interval.startDate, interval.endDate)) {
            console.error(`Not valid DateRange interval: "${interval.text}"\n  StartDate: ${interval.startDate} - EndDate: ${interval.endDate}`);
            return;
        }
        this.validIntervals.push(interval);
    }
    /**
     * Evaluates if an backIntervalFromToday expression is valid.
     * @param intervalExp backIntervalFromToday expression.
     * @returns True if the backIntervalFromToday expression is valid.
     */
    isValidBackIntervalFromToday(intervalExp) {
        const intervalType = intervalExp.at(-1) || '';
        const intervalValue = parseInt(intervalExp.slice(0, intervalExp.length - 1), 10);
        const isValidType = (['y', 'm', 'd'].includes(intervalType));
        const isValidValue = Number.isInteger(intervalValue);
        return isValidType && isValidValue;
    }
    /**
     * Return if start and end date interval is valid.
     * @param startDate Start Date string.
     * @param endDate End Date string.
     * @returns Return true if start an end date are valid dates and if start is before end.
     */
    isValidStartAndEndDate(startDate, endDate) {
        return (this.isValidDate(startDate)
            && this.isValidDate(endDate)
            && this.isStartDateBeforeEndDate(startDate, endDate));
    }
    /**
     * Return if start date before end date.
     * @param startDate Start Date string.
     * @param endDate End Date string.
     * @returns True if startDate is before endDate.
     */
    isStartDateBeforeEndDate(startDate, endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return start <= end;
    }
    /**
     * Return if dateString is a valid date.
     * @param dateString Date string.
     * @returns True if dateString is a valid date.
     */
    isValidDate(dateString) {
        const timestamp = Date.parse(dateString);
        return !Number.isNaN(timestamp);
    }
    changeValueFromStartEndDate(startDate, endDate) {
        this.startDate = DateHelper.getYearMonthDayString(new Date(startDate));
        this.endDate = DateHelper.getYearMonthDayString(new Date(endDate));
    }
    /**
     * Emits the dates changed event with the start and end date.
     */
    emitDatesChangedEvent() {
        this.dispatchEvent(new CustomEvent('dates-changed', {
            detail: {
                startDate: this.startDate,
                endDate: this.endDate,
                intervalPosition: this.intervalSelected,
            },
        }));
    }
    /**
     * The createRenderRoot function is a function that is called by the lit-html
     * library to tell the component where to render the html.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    /**
     * Renders the template literal HTML of the component.
     * @returns The HTML template literal.
     */
    render() {
        return html `
      <div class="date-range-intervals" id="${this.intervalsId}">
        <div class="date-range-intervals-scrollview" id="${this.intervalsId}-scrollview">
          <div class="date-range-intervals-scrollview-content" id="${this.intervalsId}-scrollview-content"></div>
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], DinapsisDateRangeIntervals.prototype, "intervalsId", void 0);
__decorate([
    property({ type: Array() })
], DinapsisDateRangeIntervals.prototype, "intervals", void 0);
__decorate([
    property({ type: Number })
], DinapsisDateRangeIntervals.prototype, "intervalSelected", void 0);
DinapsisDateRangeIntervals = __decorate([
    customElement('dinapsis-date-range-intervals')
], DinapsisDateRangeIntervals);
export { DinapsisDateRangeIntervals };
//# sourceMappingURL=dinapsis-date-range-intervals.js.map